"use strict";

const _ = require('lodash');

class ButtonControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("button-control");
        this._rootElem.tabIndex = 0;
        this._rootElem.component = this._model;
        this._onKeyPress = this._onKeyPress.bind(this)
        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }

    _registerListeners(){
        this._model.on("Disabled",this._onDisabled = this._onDisabled.bind(this));
        this._model.on("Invalid",this._onInvalid = this._onInvalid.bind(this));
        this._model.on("Label",this._onLabel = this._onLabel.bind(this));
        this._model.on("Value",this._onValue = this._onValue.bind(this));
        this._model.on("Visible",this._onVisible = this._onVisible.bind(this));
        this._model.on("Active",this._onActive = this._onActive.bind(this));
        this._model.on("DarkTheme",this._onDarkTheme = this._onDarkTheme.bind(this));
        this._model.on("Theme",this._onTheme = this._onTheme.bind(this));
        this._model.on("Focus",this._onFocus = this._onFocus.bind(this));
        this._model.on("Blur",this._onBlur = this._onBlur.bind(this));
        this._model.on("SetAttribute",this._onSetAttribute = this._onSetAttribute.bind(this));
        this._rootElem.addEventListener("click", this._onClickEvent = this._onClickEvent.bind(this));
        this._rootElem.addEventListener('focus', this._onFocusEvent = this._onFocusEvent.bind(this));
        this._rootElem.addEventListener('blur', this._onBlurEvent = this._onBlurEvent.bind(this));
        this._rootElem.addEventListener('keydown', this._onKeyDownEvent = this._onKeyDownEvent.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("Disabled",this._onDisabled);
        this._model.removeListener("Invalid",this._onInvalid);
        this._model.removeListener("Label",this._onLabel);
        this._model.removeListener("Value",this._onValue);
        this._model.removeListener("Visible",this._onVisible);
        this._model.removeListener("Active",this._onActive);
        this._model.removeListener("DarkTheme",this._onDarkTheme);
        this._model.removeListener("Theme",this._onTheme);
        this._model.removeListener("Focus",this._onFocus);
        this._model.removeListener("Blur",this._onBlur);
        this._model.removeListener("SetAttribute",this._onSetAttribute);
        this._rootElem.removeEventListener("click", this._onClickEvent);
        this._rootElem.removeEventListener('focus', this._onFocusEvent);
        this._rootElem.removeEventListener('blur', this._onBlurEvent);
        this._rootElem.removeEventListener('keydown', this._onKeyDownEvent);
        this._rootElem.removeEventListener("keypress",this._onKeyPress);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setActive(this._model.active);
            this._setDisabled(this._model.disabled);
            this._setInvalid(this._model.invalid);
            this._setLabel(this._model.label);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            if(this._model._title) {
                this._setAttribute("title", this._model._title);
            }
            if(this._model._class) {
                this._rootElem.classList.add(this._model._class);
            }
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal){
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal){
        this._setInvalid(newVal);
    }

    /**
     * Fired when the label has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onLabel(newVal){
        this._setLabel(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param oldVal {String}
     * @param newVal {String}
     * @private
     */
    _onValue(oldVal, newVal){
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal){
        this._setVisible(newVal);
    }

    /**
     * Fired when the active status has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onActive(newVal){
        this._setActive(newVal);
    }

    /**
     * Fired when the dark theme state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal){
        this._setDarkTheme(newVal);
    }
    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus(){
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur(){
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue){
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when the control is clicked
     * @param event {Event}
     * @private
     */
    _onClickEvent(event){
        if (this._model.disabled) {
            event.stopImmediatePropagation();
        } else {
            this._model._triggerUserClick(event);
        }
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event){
        this._model._triggerUserFocus(event);
        this._rootElem.addEventListener("keypress",this._onKeyPress);
    }

    /**
     * Listen for enter key to press button on tab focus
     * @param evt
     * @private
     */
    _onKeyPress(evt){
        switch (evt.keyCode){
            case 13:
                this._onClickEvent(evt);
                break;
        }
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event){
        this._rootElem.removeEventListener("keypress",this._onKeyPress);
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event){
        this._model._triggerUserKeyDown(event);
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal){
        if (newVal === true) {
            this._rootElem.setAttribute('disabled', true);
            this._rootElem.tabIndex = -1;
        } else {
            this._rootElem.removeAttribute('disabled');
            this._rootElem.tabIndex = 0;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('invalid');
        } else {
            this._rootElem.classList.remove('invalid');
        }
    }

    /**
     * Toggle the display text value (label) of the button
     * @param newVal {String}
     * @private
     */
    _setLabel(newVal){
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._rootElem.innerHTML = newVal;
        } else {
            this._rootElem.innerHTML = '';
        }
    }

    /**
     * Toggle the display text value of the button
     * @param newVal {String}
     * @private
     */
    _setValue(newVal){
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._rootElem.setAttribute('value', newVal);
        } else {
            this._rootElem.removeAttribute('value');
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal){
        if (newVal === true) {
            this._rootElem.classList.remove('hidden');
        } else {
            this._rootElem.classList.add('hidden');
        }
    }

    /**
     * Toggle the active state
     * @param newVal {Boolean}
     * @private
     */
    _setActive(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('active');
        } else {
            this._rootElem.classList.remove('active');
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('dark');
        } else {
            this._rootElem.classList.remove('dark');
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal){
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal){
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue){
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ButtonControlRenderer;
